import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { css, Theme } from '@emotion/react';
import MuiTab from '@mui/material/Tab';
import MuiTabs from '@mui/material/Tabs';
import { Typography } from 'client/components/Typography';
import { TabProps, tabStyle, tabTextStyle } from './Tab';

export interface TabsProps {
  items: TabProps[];
  selected?: string;

  onSelect?: (item: TabProps) => void;
}

export const Tabs = ({ items, selected: selectedProp, onSelect }: TabsProps) => {
  const [selected, setSelected] = useState(selectedProp || (items.length ? items[0].id : null));

  useEffect(() => {
    setSelected(selectedProp || null);
  }, [selectedProp]);

  const handleSelect = useCallback(
    (event: any, value: string) => {
      const item = items.find((item) => item.id === value);
      if (!item) return;

      if (onSelect) onSelect(item);

      // Don't update the selected tab when using links, otherwise the confirmation popup for unsaved changes won't work
      if (!item.href) setSelected(value);
    },
    [items, onSelect]
  );

  const renderItem = useCallback(
    (tab: TabProps) => (
      <MuiTab
        key={tab.id}
        value={tab.id}
        css={tabStyle}
        disabled={tab.disabled}
        disableRipple
        {...(tab.href
          ? {
              component: Link,
              to: tab.href,
              replace: true,
            }
          : {})}
        label={
          <Typography variant="body1" css={tabTextStyle}>
            {tab.icon && <tab.icon.type weight="fill" size={20} />}
            {tab.title}
          </Typography>
        }
      />
    ),
    []
  );

  // TODO: Tabpanel & aria stuff
  return (
    <MuiTabs value={selected} TabIndicatorProps={{ children: <span css={tabsIndicatorStyle} /> }} css={tabsStyle} onChange={handleSelect}>
      {items.map(renderItem)}
    </MuiTabs>
  );
};

const tabsStyle = (theme: Theme) => css`
  .MuiTabs-flexContainer {
    justify-content: center;
    gap: 8px;

    ${theme.mediaQuery.large(css`
      justify-content: flex-start;
    `)}
  }

  .MuiTabs-indicator {
    display: flex;
    justify-content: center;
    height: 4px;
    background-color: transparent;
    pointer-events: none;
  }
`;

const tabsIndicatorStyle = (theme: Theme) => css`
  height: 8px;
  width: 60%;
  max-width: 6rem;
  background-color: ${theme.color.primary[100]};
  border-radius: 8px;
  z-index: 1;
`;
